import { useEffect, useState, useCallback } from "react";
import { useAuth, useNivel4Overline } from "@/context/AuthContext";
import api, { formatError, descargarRecurso } from "@/lib/api";
import { PageHeader, ScopeNotice, useScopeGuard, useCan } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Download, FolderArchive, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Informes() {
  const { user, scopeTenant } = useAuth();
  const overlineN4 = useNivel4Overline();
  const canEdit = useCan(user);
  const needsScope = useScopeGuard(user, scopeTenant);
  const [informes, setInformes] = useState([]);
  const [exports, setExports] = useState([]);
  const [simulacros, setSimulacros] = useState([]);
  const [gen, setGen] = useState(false);
  const [zip, setZip] = useState(false);

  const load = useCallback(async () => {
    if (needsScope) return;
    const [i, e, s] = await Promise.all([api.get("/informes"), api.get("/auditoria/exports"), api.get("/simulacros")]);
    setInformes(i.data); setExports(e.data); setSimulacros(s.data);
  }, [needsScope]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const generando = exports.some((e) => e.estado === "generando");
    if (!generando) return;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [exports, load]);

  const generar = async () => {
    setGen(true);
    try { await api.post("/informes/preauditoria"); toast.success("Informe de pre-auditoría generado"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setGen(false); }
  };

  const exportarZip = async () => {
    setZip(true);
    try { await api.post("/auditoria/exportar"); toast.success("Preparando carpeta de auditoría…"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setZip(false); }
  };

  const descargar = async (tipo, id) => {
    try { await descargarRecurso(tipo, id); } catch { toast.error("No se pudo generar la descarga"); }
  };

  if (needsScope) return <ScopeNotice />;

  return (
    <div data-testid="informes-page">
      <PageHeader overline={overlineN4} title="Informes de Pre-Auditoría" help="informes"
        action={canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportarZip} disabled={zip} data-testid="exportar-zip-btn" className="border-[#0E2A47] text-[#0E2A47]">
              <FolderArchive size={16} className="mr-1" /> {zip ? "Preparando…" : "Carpeta de auditoría (ZIP)"}
            </Button>
            <Button onClick={generar} disabled={gen} data-testid="generar-informe-btn" className="bg-[#FF5625] hover:bg-[#E0451A]">
              <FileText size={16} className="mr-1" /> {gen ? "Generando…" : "Generar informe PDF"}
            </Button>
          </div>
        )} />

      <Card className="border-[#E2E8F0] overflow-hidden mb-6">
        <div className="px-4 py-2 bg-[#F7F8FA] text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Informes de readiness</div>
        <Table>
          <TableHeader><TableRow className="bg-white">
            <TableHead>Fichero</TableHead><TableHead>Readiness</TableHead><TableHead>Fecha</TableHead><TableHead className="text-right">Acción</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {informes.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-[#0E2A47]/50 py-8">Sin informes generados.</TableCell></TableRow>}
            {informes.map((inf) => (
              <TableRow key={inf.id} data-testid={`informe-row-${inf.id}`} className="hover:bg-[#F7F8FA]">
                <TableCell className="font-mono text-xs">{inf.filename}</TableCell>
                <TableCell><Badge className="bg-[#0E2A47] hover:bg-[#0E2A47]">{inf.readiness}%</Badge></TableCell>
                <TableCell className="text-[#0E2A47]/60 text-sm">{new Date(inf.creado_en).toLocaleString("es-ES")}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => descargar("informe", inf.id)} data-testid={`informe-descargar-${inf.id}`}><Download size={15} /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="border-[#E2E8F0] overflow-hidden">
        <div className="px-4 py-2 bg-[#F7F8FA] text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Carpetas de auditoría (ZIP)</div>
        <Table>
          <TableHeader><TableRow className="bg-white">
            <TableHead>Fichero</TableHead><TableHead>Estado</TableHead><TableHead>Docs</TableHead><TableHead>Fecha</TableHead><TableHead className="text-right">Acción</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {exports.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-[#0E2A47]/50 py-8">Sin carpetas generadas.</TableCell></TableRow>}
            {exports.map((e) => (
              <TableRow key={e.id} data-testid={`export-row-${e.id}`} className="hover:bg-[#F7F8FA]">
                <TableCell className="font-mono text-xs">{e.filename || "—"}</TableCell>
                <TableCell>
                  {e.estado === "generando" && <Badge className="bg-[#F59E0B] hover:bg-[#F59E0B]"><Loader2 size={12} className="mr-1 animate-spin" />Generando</Badge>}
                  {e.estado === "completado" && <Badge className="bg-[#10B981] hover:bg-[#10B981]">Completado</Badge>}
                  {e.estado === "error" && <Badge className="bg-[#EF4444] hover:bg-[#EF4444]">Error</Badge>}
                </TableCell>
                <TableCell>{e.num_documentos ?? "—"}</TableCell>
                <TableCell className="text-[#0E2A47]/60 text-sm">{new Date(e.creado_en).toLocaleString("es-ES")}</TableCell>
                <TableCell className="text-right">
                  {e.estado === "completado" && <Button size="sm" variant="ghost" onClick={() => descargar("auditoria_zip", e.id)} data-testid={`export-descargar-${e.id}`}><Download size={15} /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="border-[#E2E8F0] overflow-hidden mt-6">
        <div className="px-4 py-2 bg-[#F7F8FA] text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Simulacros de retirada</div>
        <Table>
          <TableHeader><TableRow className="bg-white">
            <TableHead>Lote origen</TableHead><TableHead>Duración</TableHead><TableHead>Localizado</TableHead><TableHead>Ejecutor</TableHead><TableHead>Fecha</TableHead><TableHead className="text-right">Acción</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {simulacros.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-[#0E2A47]/50 py-8">Sin simulacros ejecutados.</TableCell></TableRow>}
            {simulacros.map((s) => (
              <TableRow key={s.id} data-testid={`simulacro-row-${s.id}`} className="hover:bg-[#F7F8FA]">
                <TableCell className="font-mono text-xs">{s.lote_codigo}</TableCell>
                <TableCell><Badge className="bg-[#3B82F6] hover:bg-[#3B82F6]">{s.duracion_segundos}s</Badge></TableCell>
                <TableCell><Badge className="bg-[#10B981] hover:bg-[#10B981]">{s.pct_localizado}%</Badge></TableCell>
                <TableCell className="text-[#0E2A47]/70 text-sm">{s.ejecutor_nombre}</TableCell>
                <TableCell className="text-[#0E2A47]/60 text-sm">{new Date(s.creado_en).toLocaleString("es-ES")}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => descargar("simulacro", s.id)} data-testid={`simulacro-descargar-${s.id}`}><Download size={15} /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
